// Vendor Libs
import {chain, cloneDeep, defaultsDeep, findIndex, get, isEmpty, mapValues, now, set} from 'lodash';

// Config
import getBootstrapInitialState from '../bootstrap-data/services-initial-states';
import {applyPreferenceState} from '../../config/preferences-config';

const initialState = {routerParams: {}};
let preferenceStates = {};

function getServiceInitialState(servicesDataKey) {
    const state = getBootstrapInitialState(servicesDataKey);
    const preferenceState = preferenceStates[servicesDataKey];

    return applyPreferenceState('servicesSearchData', preferenceState, state, servicesDataKey);
}

const actions = {
    SEARCH_DATA_REGISTER_STORE: (state, {servicesDataKey}) =>
        !state[servicesDataKey]
            ? set(cloneDeep(state), servicesDataKey, getServiceInitialState(servicesDataKey))
            : state,

    SEARCH_DATA_UNREGISTER_STORE: (state, {servicesDataKey}) =>
        chain(state)
            .cloneDeep()
            .unset(servicesDataKey)
            .value(),

    SEARCH_DATA_FETCH: (state, {servicesDataKey}) => {
        const newState = cloneDeep(state);

        return defaultsDeep(
            {
                [servicesDataKey]: {
                    error: null,
                    fetched: false,
                    fetching: true,
                },
            },
            newState
        );
    },

    SEARCH_DATA_FETCH_REJECTED: (state, {error, servicesDataKey}) => {
        const newState = cloneDeep(state);

        set(newState, [servicesDataKey, 'searchData', 'content'], []);
        return defaultsDeep(
            {
                [servicesDataKey]: chain(getServiceInitialState(servicesDataKey))
                    .set('error', error)
                    .set('fetching', false)
                    .value(),
            },
            newState
        );
    },

    SEARCH_DATA_FETCH_FULFILLED: (state, {activeRow, path, query, searchData, servicesDataKey}) => {
        const newState = defaultsDeep(
            {
                [servicesDataKey]: {
                    error: null,
                    fetching: false,
                    fetched: true,
                    lastUpdated: now(),
                },
            },
            cloneDeep(state)
        );

        Object.assign(newState[servicesDataKey], {
            activeRow,
            path,
            query,
            searchData,
        });

        return newState;
    },

    SEARCH_DATA_SORT: (state, {order, sortColumn, servicesDataKey}) => {
        const newState = state[servicesDataKey]
            ? cloneDeep(state)
            : {...cloneDeep(state), ...{[servicesDataKey]: getServiceInitialState(servicesDataKey)}};
        const sort = get(newState, [servicesDataKey, 'query', 'sort']) || [];
        const sortIndex = findIndex(sort, sortObj => sortObj.column === sortColumn);
        const newSort = sort.slice(0);
        const newSortObj = {
            column: sortColumn,
            order: order || getNextSort(get(newSort[sortIndex], 'order')),
        };

        if (sortIndex > -1) {
            newSortObj.order ? newSort.splice(sortIndex, 1, newSortObj) : newSort.splice(sortIndex, 1);
        } else {
            newSort.push(newSortObj);
        }

        return set(newState, [servicesDataKey, 'query', 'sort'], newSort);
    },

    SEARCH_DATA_SORT_RESET: (state, {servicesDataKey, clearSort, actionShouldFetch}) =>
        chain(state)
            .cloneDeep()
            .set(
                [servicesDataKey, 'query', 'sort'],
                clearSort ? [] : get(getBootstrapInitialState(servicesDataKey), ['query', 'sort'], [])
            )
            .set([servicesDataKey, 'query', 'page'], 1)
            .value(),

    SEARCH_DATA_SET_FILTER: (state, {servicesDataKey, activeId, targets}) =>
        servicesDataKey && state[servicesDataKey]
            ? set(cloneDeep(state), [servicesDataKey, 'filter'], {
                  activeId,
                  targets,
              })
            : state,

    SEARCH_DATA_SET_QUERY: (state, {servicesDataKey, query}) => {
        const newState = cloneDeep(state);

        return defaultsDeep(
            {
                [servicesDataKey]: {
                    query: {...newState[servicesDataKey].query, ...query},
                },
            },
            newState
        );
    },

    SEARCH_DATA_RESET: (state, {servicesDataKey, resetStore}) => {
        let newState = cloneDeep(state);

        if (!isEmpty(servicesDataKey)) {
            set(newState, servicesDataKey, getServiceInitialState(servicesDataKey));
            set(newState, [servicesDataKey, 'query'], {
                page: 1,
                pageLimit: 20,
            });
        } else if (!resetStore) {
            newState = mapValues(state, serviceKey => getBootstrapInitialState(serviceKey));
        } else {
            newState = cloneDeep(initialState);
        }

        return newState;
    },

    SEARCH_DATA_SEARCH: (state, {servicesDataKey}) =>
        chain(state)
            .cloneDeep()
            .set([servicesDataKey, 'query', 'page'], 1)
            .set([servicesDataKey, 'filter'], {})
            .value(),

    SEARCH_DATA_ROUTER_CHANGED: (state, {routerParams}) => ({...cloneDeep(state), routerParams}),

    GLOBAL_SHELVES_TREE_FETCH_REJECTED: (state, {error}) => defaultsDeep({error}, cloneDeep(state)),

    GLOBAL_SHELVES_TREE_FETCH_FULFILLED: (state, shelvesTreeData) => defaultsDeep(shelvesTreeData, cloneDeep(state)),

    PREFERENCES_LOAD: (state, {stores}) => {
        preferenceStates = get(stores, 'servicesSearchData', {});

        return state;
    },

    PREFERENCES_SAVE: (state, {stores}) => {
        preferenceStates = get(stores, 'servicesSearchData', {});

        return state;
    },

    PREFERENCES_RESET: state => {
        preferenceStates = {};

        return state;
    },
};

const getNextSort = sortType => {
    let nextSortType;

    if (!sortType) {
        nextSortType = 'asc';
    } else if (sortType === 'asc') {
        nextSortType = 'desc';
    }

    return nextSortType;
};

const reducer = (state = initialState, action) => (actions[action.type] ? actions[action.type](state, action) : state);

export default reducer;
